import { useState, useEffect, startTransition } from "react"; // Import startTransition
import { useProfileStore } from './useProfileStore';
import { User } from "firebase/auth"; // Import User type

type UserRole = "admin" | "staff" | "client" | "limited_access" | null; // Define possible roles
export type { UserRole };

interface UseUserRoleArgs {
  user: User | null;
  authLoading: boolean;
}

interface UseUserRoleReturn {
  role: UserRole;
  loading: boolean;
  error: Error | null;
}

// Accept user and authLoading as arguments
export function useUserRole({ user, authLoading }: UseUserRoleArgs): UseUserRoleReturn {
  // No longer need to call useCurrentUser here
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState<boolean>(true); // Role loading state - Start true initially
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // If auth is still loading, do nothing yet. Role loading remains true.
    if (authLoading) {
      // Keep role loading true while auth is resolving
      startTransition(() => {
        setLoading(true);
      });
      return;
    }

    // If auth finished and there is no user, set role to null and stop loading.
    if (!user) {
      startTransition(() => {
        setRole(null);
        setLoading(false); // Stop role loading
        setError(null); // Clear any previous error
      });
      return;
    }

    // ---- User exists and auth loading is finished ----
    // Use the profile store instead of creating a duplicate Firestore listener
    const { profile } = useProfileStore.getState();
    
    // If profile is already loaded, use it immediately
    if (profile && profile.role) {
      startTransition(() => {
        setRole(profile.role);
        setLoading(false);
        setError(null);
      });
    }
    
    // Subscribe to profile store changes
    const unsubscribeStore = useProfileStore.subscribe((state) => {
      if (state.profile && state.profile.role) {
        startTransition(() => {
          setRole(state.profile.role);
          setLoading(false);
          setError(null);
        });
      } else if (state.error && !state.isLoading) {
        // Fallback to backend API if profile store fails
        console.warn('⚠️ useUserRole: Profile store error, trying backend API...');
        fetch(`${window.location.origin}/api/debug/user/${user.uid}`)
          .then(res => res.json())
          .then(data => {
            if (data.exists && data.data && data.data.role) {
              console.log('✅ useUserRole: Loaded role from backend API after error:', data.data.role);
              startTransition(() => {
                setRole(data.data.role);
                setError(null);
                setLoading(false);
              });
            } else {
              startTransition(() => {
                setRole(null);
                setError(new Error("Failed to load user role"));
                setLoading(false);
              });
            }
          })
          .catch(apiError => {
            console.error('❌ useUserRole: Backend API fallback failed:', apiError);
            startTransition(() => {
              setRole(null);
              setError(new Error("Failed to load user role"));
              setLoading(false);
            });
          });
      }
    });
    
    // Cleanup function
    return () => {
      unsubscribeStore();
    };
  }, [user, authLoading]); // Re-run if user object or auth loading state changes

  // Role loading depends only on the Firestore fetch now
  // Auth loading is handled by AppProvider or UserGuard
  return { role, loading, error };
}
